import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatApiErrorDetail, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ForkKnife, Lock, ArrowClockwise, Drop, Camera, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function MealPlan() {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [isSample, setIsSample] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploadForm, setUploadForm] = useState({ meal_date: new Date().toISOString().slice(0,10), meal_name: "", notes: "" });
  const [uploading, setUploading] = useState(false);
  const [blobUrls, setBlobUrls] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/plans/meal");
      setPlan(data.content);
      setIsSample(data.is_sample);
    } catch (e) { toast.error("Could not load plan"); }
  };

  const loadPhotos = async () => {
    try {
      const { data } = await api.get("/meal-photos");
      setPhotos(data);
      // Fetch blob URLs for display
      const urls = {};
      for (const p of data.slice(0, 12)) {
        try {
          const resp = await api.get(`/files/${p.storage_path}`, { responseType: 'blob' });
          urls[p.id] = URL.createObjectURL(resp.data);
        } catch {}
      }
      setBlobUrls(urls);
    } catch {}
  };

  useEffect(() => {
    load();
    loadPhotos();
    return () => { Object.values(blobUrls).forEach((u) => URL.revokeObjectURL(u)); };
    // eslint-disable-next-line
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/plans/generate", { plan_type: "meal" });
      setPlan(data.content);
      setIsSample(false);
      toast.success("Fresh meal plan generated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setGenerating(false); }
  };

  const uploadPhoto = async (fileInput) => {
    const file = fileInput.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error("Max 6MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("meal_date", uploadForm.meal_date);
      fd.append("meal_name", uploadForm.meal_name);
      fd.append("notes", uploadForm.notes);
      await api.post("/meal-photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadForm({ ...uploadForm, meal_name: "", notes: "" });
      fileInput.target.value = "";
      loadPhotos();
      toast.success("Meal logged — AI will use this next generation");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setUploading(false); }
  };

  const deletePhoto = async (id) => {
    await api.delete(`/meal-photos/${id}`);
    if (blobUrls[id]) URL.revokeObjectURL(blobUrls[id]);
    setBlobUrls({ ...blobUrls, [id]: undefined });
    setPhotos(photos.filter((p) => p.id !== id));
  };

  const isFree = user?.subscription_tier === "free";

  return (
    <Layout>
      <div className="p-8 md:p-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs font-heading uppercase tracking-widest text-[#FF3B30]">/ Fuel</div>
            <h1 className="font-heading font-black uppercase text-5xl mt-2 flex items-center gap-4">
              <ForkKnife size={48} weight="duotone" className="text-[#FF3B30]" /> Meal Plan
            </h1>
          </div>
          {!isFree && (
            <Button onClick={generate} disabled={generating || !user?.profile} data-testid="generate-meal"
              className="rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold h-11 px-6">
              <ArrowClockwise size={18} weight="bold" className={`mr-2 ${generating ? "animate-spin" : ""}`} />
              {generating ? "Generating…" : plan && !isSample ? "Regenerate" : "Generate"}
            </Button>
          )}
        </div>

        {isSample && (
          <div className="border border-[#FF3B30] bg-[#FF3B30]/10 p-6 mb-6 flex items-center justify-between" data-testid="sample-notice">
            <div>
              <div className="font-heading uppercase font-bold">Sample Meal</div>
              <div className="text-sm text-[#A3A3A3]">Full daily plan available on Pro or Elite.</div>
            </div>
            <Link to="/pricing" className="border border-[#FF3B30] bg-[#FF3B30] text-white px-6 py-3 font-heading uppercase tracking-widest text-sm">Upgrade</Link>
          </div>
        )}

        {plan ? (
          <div className="space-y-6">
            {plan.summary && (
              <div className="border border-[#262626] bg-[#121212] p-6">
                <p className="text-sm">{plan.summary}</p>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <MacroCard label="Calories" value={plan.daily_calories} />
              <MacroCard label="Protein" value={plan.protein_g} unit="g" />
              <MacroCard label="Carbs" value={plan.carbs_g} unit="g" />
              <MacroCard label="Fats" value={plan.fats_g} unit="g" />
              <MacroCard label="Water" value={plan.hydration_liters} unit="L" icon={Drop} />
            </div>

            <div className="grid gap-4">
              {(plan.meals || []).map((m, i) => (
                <div key={i} className="border border-[#262626] bg-[#121212] p-6" data-testid={`meal-${i}`}>
                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-[#262626]">
                    <div>
                      <div className="font-heading font-black text-2xl uppercase">{m.name}</div>
                      {m.time && <div className="text-xs uppercase tracking-widest text-[#FF3B30] mt-1">{m.time}</div>}
                    </div>
                    <div className="text-right">
                      {m.calories && <div className="font-heading font-bold text-xl">{m.calories}</div>}
                      {m.macros && <div className="text-xs text-[#A3A3A3]">{m.macros}</div>}
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm">
                    {(m.items || []).map((it, j) => (
                      <li key={j} className="flex gap-3">
                        <span className="text-[#FF3B30]">▸</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {plan.locked_meals && (
              <div className="border-2 border-dashed border-[#262626] p-8 text-center">
                <Lock size={32} className="text-[#A3A3A3] mx-auto mb-3" />
                <div className="font-heading uppercase tracking-widest text-sm text-[#A3A3A3]">{plan.locked_meals} more meals locked</div>
                <Link to="/pricing" className="mt-4 inline-block border border-[#FF3B30] bg-[#FF3B30] text-white px-6 py-3 font-heading uppercase tracking-widest text-sm">Unlock Full Plan</Link>
              </div>
            )}

            {plan.shopping_list && (
              <div className="border border-[#262626] bg-[#121212] p-6">
                <div className="text-xs font-heading uppercase tracking-widest text-[#A3A3A3] mb-3">Shopping List</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  {plan.shopping_list.map((s, i) => <div key={i} className="text-[#A3A3A3]">▪ {s}</div>)}
                </div>
              </div>
            )}
          </div>
        ) : (
          !isFree && user?.profile && (
            <div className="border-2 border-dashed border-[#262626] p-16 text-center">
              <ForkKnife size={48} className="text-[#A3A3A3] mx-auto mb-4" />
              <div className="font-heading uppercase tracking-widest text-[#A3A3A3]">No meal plan yet</div>
              <Button onClick={generate} disabled={generating}
                className="mt-6 rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold" data-testid="generate-first-meal">
                {generating ? "Generating…" : "Generate My Fuel Plan"}
              </Button>
            </div>
          )
        )}

        {/* Meal Photos - what athletes actually ate */}
        {!isFree && (
          <div className="mt-10 border border-[#262626] bg-[#121212]" data-testid="meal-photos-section">
            <div className="p-6 border-b border-[#262626] flex items-center gap-3">
              <Camera size={24} weight="duotone" className="text-[#FF3B30]" />
              <div>
                <h2 className="font-heading font-bold uppercase text-lg">What You Actually Ate</h2>
                <p className="text-xs text-[#A3A3A3] mt-1">Snap your meals. AI tightens the next macro cycle based on what's real.</p>
              </div>
            </div>
            <div className="p-6 grid md:grid-cols-4 gap-3">
              <div>
                <Label className="font-heading uppercase tracking-widest text-xs">Date</Label>
                <Input type="date" value={uploadForm.meal_date} onChange={(e) => setUploadForm({ ...uploadForm, meal_date: e.target.value })}
                  data-testid="photo-date" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" />
              </div>
              <div>
                <Label className="font-heading uppercase tracking-widest text-xs">Meal</Label>
                <Input value={uploadForm.meal_name} onChange={(e) => setUploadForm({ ...uploadForm, meal_name: e.target.value })}
                  placeholder="Breakfast, Post-training…" data-testid="photo-meal"
                  className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" />
              </div>
              <div>
                <Label className="font-heading uppercase tracking-widest text-xs">Notes</Label>
                <Input value={uploadForm.notes} onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
                  placeholder="Portions, macros estimate" data-testid="photo-notes"
                  className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626] h-11" />
              </div>
              <div className="flex items-end">
                <label className="w-full cursor-pointer">
                  <input type="file" accept="image/*" onChange={uploadPhoto} disabled={uploading} className="hidden" data-testid="photo-upload" />
                  <div className={`border-2 border-dashed p-3 text-center font-heading uppercase tracking-widest text-sm ${
                    uploading ? "border-[#A3A3A3] text-[#A3A3A3]" : "border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10"
                  }`}>
                    {uploading ? "Uploading…" : "Snap / Upload"}
                  </div>
                </label>
              </div>
            </div>
            {photos.length > 0 && (
              <div className="px-6 pb-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {photos.map((p) => (
                  <div key={p.id} className="border border-[#262626] bg-[#0A0A0A] group relative" data-testid={`photo-${p.id}`}>
                    {blobUrls[p.id] ? (
                      <img src={blobUrls[p.id]} alt={p.meal_name} className="w-full h-32 object-cover" />
                    ) : (
                      <div className="w-full h-32 flex items-center justify-center text-[#A3A3A3] text-xs">Loading…</div>
                    )}
                    <div className="p-2">
                      <div className="text-xs font-heading uppercase tracking-widest text-[#FF3B30] truncate">{p.meal_name || "Meal"}</div>
                      <div className="text-[10px] text-[#A3A3A3]">{p.meal_date}</div>
                      {p.notes && <div className="text-[10px] text-[#A3A3A3] italic truncate">{p.notes}</div>}
                    </div>
                    <button onClick={() => deletePhoto(p.id)} data-testid={`del-photo-${p.id}`}
                      className="absolute top-1 right-1 bg-black/70 p-1 opacity-0 group-hover:opacity-100 transition-opacity text-[#FF3B30]">
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

function MacroCard({ label, value, unit = "", icon: Icon }) {
  return (
    <div className="border border-[#262626] bg-[#121212] p-4 text-center" data-testid={`macro-${label.toLowerCase()}`}>
      {Icon && <Icon size={18} weight="duotone" className="text-[#FF3B30] mx-auto mb-2" />}
      <div className="font-heading font-black text-3xl">{value ?? "—"}{unit && <span className="text-lg text-[#A3A3A3]">{unit}</span>}</div>
      <div className="text-xs uppercase tracking-widest text-[#A3A3A3] mt-1">{label}</div>
    </div>
  );
}
